import { createAction, createSlice } from '@reduxjs/toolkit';
import { setSharedValue, setFilter, setTableLoadingState } from '@/pages/shared/shared.reducer';
import { runCheckerSummaryMutation } from '@/pages/checker-summary/checker-summary.query';
import { runApprovalSummaryMutation } from '@/pages/approval-summary/approval-summary.query';
import { notifyUnlessUnauthorized, getServerErrorMessage, isUnauthorizedError } from '@/pages/shared/rtk-query-helpers';
import { openSuccessNotification, openErrorNotification } from '@/notification';
import { getNotifications } from '@/pages/login/login.reducer';

const CHECK_SUMMARY_VALUE_SET = "CHECK_SUMMARY_VALUE_SET";

const initialState = {
    authors: {
        items: [],
        loading: false
    },
    approvers: {
        items: [],
        loading: false
    },
    checkers: {
        items: [],
        loading: false
    },
    sites: {
        items: [],
        loading: false
    },
    checkingDocumentFileStatuses: {
        items: [{ statusId: 7, status: "Awaiting Check" },
        { statusId: 4, status: "Rejected" }],
        loading: false
    }
}

// Replaces immutability-helper `update.set`: assigns a dotted path, creating missing objects.
const setPathValue = (draft, path, value) => {
    const segments = String(path).split('.');
    let target = draft;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];

        if (target[segment] === null || typeof target[segment] !== 'object') {
            target[segment] = {};
        }

        target = target[segment];
    }

    target[segments[segments.length - 1]] = value;
};

// Legacy action type string is preserved so existing dispatch sites keep working.
export const setValue = createAction(CHECK_SUMMARY_VALUE_SET, (key, value) => ({
    payload: { key, value }
}));

const checkerSummarySlice = createSlice({
    name: 'checkerSummary',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(setValue, (draft, action) => {
            setPathValue(draft, `${action.payload.key}`, action.payload.value);
        });
    }
});

const checkerSummaryReducer = checkerSummarySlice.reducer;

export const loadAuthors = (model) => {
    return (dispatch) => {
        dispatch(setValue('authors.loading', true));
        runCheckerSummaryMutation(dispatch, 'getAuthorList', model)
            .then(payload => {
                dispatch(setValue(`authors.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load authors. Please reload page`);
            });
        dispatch(setValue('authors.loading', false));
    }
}

export const loadApprovers = (model) => {
    return (dispatch) => {
        dispatch(setValue('approvers.loading', true));
        runCheckerSummaryMutation(dispatch, 'getApproversList', model)
            .then(payload => {
                dispatch(setValue(`approvers.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load approvers. Please reload page`);
            });
        dispatch(setValue('approvers.loading', false));
    }
}

export const loadSites = (model) => {
    return (dispatch) => {
        dispatch(setValue('sites.loading', true));
        runCheckerSummaryMutation(dispatch, 'getSitesList', model)
            .then(payload => {
                dispatch(setValue(`sites.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load sites. Please reload page`);
            });
        dispatch(setValue('sites.loading', false));
    }
}

export const loadStatuses = (model) => {
    return (dispatch) => {
        dispatch(setValue('checkingDocumentFileStatuses.loading', true));
        runCheckerSummaryMutation(dispatch, 'getStatusesList', model)
            .then(payload => {
                dispatch(setValue('checkingDocumentFileStatuses.items', payload.map(item => ({
                    statusId: item.id,
                    status: item.name
                }))));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load statuses. Please reload page`);
            })
            .finally(() => {
                dispatch(setValue('checkingDocumentFileStatuses.loading', false));
            });
    }
}

export const getCheckers = (model) => {
    return (dispatch) => {
        dispatch(setValue('checkers.loading', true));
        runCheckerSummaryMutation(dispatch, 'getCheckersList', model)
            .then(payload => {
                dispatch(setValue(`checkers.items`, payload));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Can't load checkers. Please reload page`);
            });
        dispatch(setValue('checkers.loading', false));
    }
}

export const checkDocuments = (items, count, comment) => {
    return async (dispatch) => {
        const checks = items.map(item => { return { documentFileId: item.documentFileId, currentCheckerId: item.checker.id } });
        dispatch(setTableLoadingState('checkerSummaryTable', true));

        try {
            await runCheckerSummaryMutation(dispatch, 'checkDocuments', { documentFiles: checks, comment: comment });

            items.forEach(item => {
                openSuccessNotification(`Document ${item.documentNumber}_Rev_${item.revisionNumber} has passed Checking`);
            });
            dispatch(setFilter('checkerSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setSharedValue('checkerSummaryTable.count', count - items.length))
            dispatch(getNotifications());
            dispatch(loadSites({}));
        } catch (error) {
            notifyUnlessUnauthorized(error, getServerErrorMessage(error) || "Some problem in check");
        } finally {
            dispatch(setTableLoadingState('checkerSummaryTable', false));
        }
    }
}

export const rejectDocument = (items, count, comment) => {
    return async (dispatch) => {
        const checks = items.map(item => { return { documentFileId: item.documentFileId, currentCheckerId: item.checker.id } });
        dispatch(setTableLoadingState('checkerSummaryTable', true));

        try {
            await runCheckerSummaryMutation(dispatch, 'rejectDocument', { documentFiles: checks, comment: comment });

            items.forEach(item => {
                openSuccessNotification(`Document ${item.documentNumber}_Rev_${item.revisionNumber}  was rejected`);
            });
            dispatch(setFilter('checkerSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setSharedValue('checkerSummaryTable.count', count - items.length))
            dispatch(getNotifications());
            dispatch(loadSites({}));
        } catch (error) {
            notifyUnlessUnauthorized(error, "Some problem in reject");
        } finally {
            dispatch(setTableLoadingState('checkerSummaryTable', false));
        }
    }
}

export const reassignCheckers = (documentFiles, checkUserId) => {
    return async (dispatch) => {
        dispatch(setTableLoadingState('checkerSummaryTable', true));

        try {
            await runCheckerSummaryMutation(dispatch, 'reassignCheckers', {
                documentCheckers: documentFiles,
                checkUserId,
            });

            dispatch(setFilter('checkerSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            openSuccessNotification(`Checkers were reassigned`)
        } catch (error) {
            notifyUnlessUnauthorized(error, `Error. Can't reassign checkers`);
        } finally {
            dispatch(setTableLoadingState('checkerSummaryTable', false));
        }
    }
}

export const rollbackRevision = (documentFileIds, documentFiles, comment) => {
    return async (dispatch) => {
        dispatch(setTableLoadingState('checkerSummaryTable', true));

        try {
            const payload = await runApprovalSummaryMutation(dispatch, 'rollbackRevision', { Ids: documentFileIds, comment });

            dispatch(setFilter('checkerSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            dispatch(setFilter('checkerSummaryTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
            openSuccessNotification(payload.message);
            dispatch(getNotifications());
            dispatch(loadSites({}));
        } catch (error) {
            if (!isUnauthorizedError(error)) {
                const failedDocumentNumber = documentFiles?.[0]?.documentNumber || "selected document(s)";
                openErrorNotification(`Can't rollback ${failedDocumentNumber}`)
            }
        } finally {
            dispatch(setTableLoadingState('checkerSummaryTable', false));
        }
    }
}

export default checkerSummaryReducer;
